import { BrowserClient, Scope, defaultStackParser, makeFetchTransport } from '@sentry/browser';

const STEPS = ['widget_load', 'order_lookup', 'cognito', 'attribution_queue', 'malformed_link'] as const;
const CATEGORIES = ['unavailable', 'configuration', 'transport', 'unexpected', 'malformed_code'] as const;
export type RegistrationStep = typeof STEPS[number];
export type RegistrationCategory = typeof CATEGORIES[number];
export interface RegistrationFailure {
  step: RegistrationStep;
  category: RegistrationCategory;
  organizationId?: string;
  eventId?: string;
  orderId?: string;
}

const identifier = (value?: string) => typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value) ? value : undefined;

export const registrationFailureEvent = (failure: RegistrationFailure) => {
  if (!STEPS.includes(failure.step) || !CATEGORIES.includes(failure.category)) return undefined;
  const tags: Record<string, string> = { registration_step: failure.step, error_category: failure.category };
  const organizationId = identifier(failure.organizationId);
  const eventId = identifier(failure.eventId);
  if (organizationId) tags.organizationId = organizationId;
  if (eventId) tags.eventId = eventId;
  if (typeof failure.orderId === 'string' && /^\d{1,32}$/.test(failure.orderId)) tags.orderId = failure.orderId;
  return { message: 'Registration path failure', level: 'error' as const,
    fingerprint: ['registration-path', failure.step, failure.category], tags };
};

let client: BrowserClient | undefined;

/* A distinct unsampled client with no automatic integrations. The final
   allow-list also drops global scope, request, breadcrumb and user enrichment. */
const reportingClient = () => {
  if (client) return client;
  const dsn = process.env.GATSBY_REGISTRATION_SENTRY_DSN || process.env.GATSBY_SENTRY_DSN;
  const environment = process.env.GATSBY_REGISTRATION_SENTRY_ENVIRONMENT || process.env.GATSBY_SENTRY_ENVIRONMENT;
  if (!dsn || !environment || typeof window === 'undefined') return undefined;
  client = new BrowserClient({
    dsn, environment, sampleRate: 1, integrations: [],
    transport: makeFetchTransport, stackParser: defaultStackParser, sendDefaultPii: false,
    beforeSend(event) {
      const tags = event.tags || {};
      const safe = registrationFailureEvent({
        step: tags.registration_step as RegistrationStep,
        category: tags.error_category as RegistrationCategory,
        organizationId: tags.organizationId as string,
        eventId: tags.eventId as string,
        orderId: tags.orderId as string,
      });
      return safe ? { ...safe, type: undefined, environment, event_id: event.event_id, timestamp: event.timestamp } : null;
    },
  });
  client.init();
  return client;
};

export const reportRegistrationFailure = (failure: RegistrationFailure): string | undefined => {
  try {
    const event = registrationFailureEvent(failure);
    const target = event && reportingClient();
    if (!event || !target) return undefined;
    const scope = new Scope();
    scope.setClient(target);
    return target.captureEvent(event, {}, scope);
  } catch { return undefined; }
};
